import {CSSProperties} from "react";
import {PageSectionProps} from "@components/Page/PageSection";

export enum CMS_ACCESS_TYPE {
    PRIVATE = "PRIVATE",
    PUBLIC = "PUBLIC",
}

export enum BREAKPOINT {
    MOBILE = "md",
    TABLET = "lg",
    DESKTOP = "xl",
    MAX = "xxl"
}

export const UIBreakpoints: Record<BREAKPOINT, number> = {
    [BREAKPOINT.MOBILE]: 768,
    [BREAKPOINT.TABLET]: 1024,
    [BREAKPOINT.DESKTOP]: 1224,
    [BREAKPOINT.MAX]: 1440
};

export type BreakpointKey = BREAKPOINT.MOBILE | BREAKPOINT.TABLET | BREAKPOINT.DESKTOP | BREAKPOINT.MAX;

export const BREAKPOINT_ORDER: BreakpointKey[] = [
    BREAKPOINT.MOBILE,
    BREAKPOINT.TABLET,
    BREAKPOINT.DESKTOP,
    BREAKPOINT.MAX
];

export enum DEVICE {
    MOBILE = "mobile",
    TABLET = "tablet",
    DESKTOP = "desktop"
}

export enum BANNER_TYPE {
    STANDARD = "standard",
    STATIC = "static",
    VERTICAL = "vertical",
    SPLIT = "split"
}

export type StaticBannerTypes = BANNER_TYPE.STATIC | BANNER_TYPE.SPLIT;

export enum HORIZONTAL_ALIGN {
    CENTER = "center",
    LEFT = "left",
    RIGHT = "right"
}

export enum VERTICAL_ALIGN {
    CENTER = "center",
    TOP = "top",
    BOTTOM = "bottom"
}

export interface ImageOptions {
    objectFit: CSSProperties["objectFit"];
}

export interface CmsImage<Type = string> extends Record<string, any> {
    src?: string | null;
    type?: Type | null;
    breakpoint?: BREAKPOINT | null;
    alt?: string | null;
    options?: ImageOptions | null;
}

export enum CMS_CTA_HREF_TYPE {
    EXTERNAL = "EXTERNAL",
    INTERNAL = "INTERNAL",
}

export enum CMS_CTA_HREF_TARGET {
    BLANK = "_blank",
    SELF = "_self",
    PARENT = "_parent",
    TOP = "_top",
    FRAMENAME = "framename",
}

export interface CmsCtaHref {
    type?: CMS_CTA_HREF_TYPE | null;
    value: string | null;
    target?: CMS_CTA_HREF_TARGET | null;
}

export enum CMS_CTA_TYPE {
    LINK = "LINK",
    BUTTON = "BUTTON",
    IMAGE = "IMAGE"
}

export enum CMS_CTA_COLOR {
    PRIMARY = "primary",
    SECONDARY = "secondary",
    BLANK = "blank",
    DANGER = "danger"
}

export enum CMS_CTA_SIZE {
    DEFAULT = "DEFAULT",
    SMALL = "SMALL",
    LARGE = "LARGE",
}

export enum CMS_CTA_VARIANT {
    DEFAULT = "default",
    BLANK = "blank",
    OUTLINED = "outlined",
}

export enum CTA_STATE {
    IDLE = "idle",
    LOADING = "loading",
    DISABLED = "disabled",
}

export interface CmsCtaOptions extends Record<string, any> {
    [CTA_STATE.IDLE]?: string;
    [CTA_STATE.LOADING]?: string;
    [CTA_STATE.DISABLED]?: string;
    withDefaultIcon?: boolean;
    removeDefaultBorder?: boolean;
    linkType?: "PUBLIC_ASSET" | string;
    icon: {
        position: "left" | "right";
    }
}

export interface CmsCta<CtaOptions = CmsCtaOptions> extends Record<string, any> {
    title?: string | null;
    scope?: string | null;
    scopeParameters?: Partial<Record<string, string>>;
    text?: string | null;
    image?: CmsImage;
    type: CMS_CTA_TYPE;
    color?: CMS_CTA_COLOR | null;
    href?: CmsCtaHref;
    size?: CMS_CTA_SIZE | null;
    options?: CtaOptions | null;
}

export type CmsSectionOptions<Type = Record<string, any>> = {
    [Property in keyof Type]?: Type[Property];
} & {
    breakpoints?: Partial<Record<BREAKPOINT, Omit<CmsSectionOptions<Type>, "breakpoints">>> | null;
    bgType: PageSectionProps["bg"];
    [k: string]: any;
};

export interface CmsSection<ImageType = string, SectionOptions = Record<string, any>, TableKeys extends string = string, FilterKeys extends string = string> {
    enabled?: boolean | null;
    type?: AllSectionTypes | null;
    title?: string | null;
    subTitle?: string | null;
    text?: string | null;
    cta?: CmsCta[] | null;
    image?: CmsImage<ImageType>[] | null;
    options?: CmsSectionOptions<SectionOptions> | null;
    sections?: CmsSection<ImageType | string, SectionOptions | Record<string, any>, TableKeys | string>[] | null;
    filters?: CMSFilters<FilterKeys>;
    table?: CMSTable<TableKeys>;
}

export type CMSFilters<Keys extends string = string> = Partial<{
    [key in Keys]: CMSFilter;
}>;

export interface CMSFilter {
    label?: string;
    placeholder?: string[];
    defaults?: string | string[];
    hidden?: boolean;
}

export type CMSTable<Keys extends string = string> = Partial<{
    [key in Keys]: {
        label: string;
        options?: CMSTable<string>
    };
}>;


/**
 * Page section types representing the mainly sections of a page
 */
export enum SECTION_TYPE {
    DEFAULT = "DEFAULT",
    MORE = "MORE",
    BOOSTS_PREVIEW = "BOOSTS_PREVIEW",
    CATALOG_PREVIEW = "CATALOG_PREVIEW",
    PRE_FOOTER = "PRE_FOOTER",
    FAQ = "FAQ",
    ONBOARD_FORM = "ONBOARD_FORM",
    CONTACT_FORM = "CONTACT_FORM",
    INVOICE_PAGE = "INVOICE_PAGE",
    INVOICE_FORM = "INVOICE_FORM",
    DROPZONE = "DROPZONE",
    UPLOADED_MODAL = "UPLOADED_MODAL",
    MEDIA = "MEDIA",
    BRAND = "BRAND",
    TITLE = "TITLE",
    CATEGORY = "CATEGORY",
    REDEEM_FORM = "REDEEM_FORM",
    DESCRIPTION = "DESCRIPTION",
    BRAND_DESCRIPTION = "BRAND_DESCRIPTION",
    T_AND_C = "T_AND_C",
}

export type AllSectionTypes =
    SECTION_TYPE;

export type CmsBanner = CmsSection;

export interface CmsPageOptions extends Record<string, any> {
}

export type CmsAnimationsOptions = Partial<Record<DEVICE, CmsPageAnimation>>;

export interface CmsPageAnimation {
    src: string;
    start?: string;
    image?: string;
}

export interface CmsPage {
    enabled?: boolean | null;
    title?: string | null;
    subTitle?: string | null;
    text?: string | null;
    banner?: CmsBanner | null;
    sections?: CmsSection[] | null;
    options?: CmsPageOptions;
}

export interface CmsPageWrapper {
    enabled: boolean;
    page?: CmsPage | null;
    section?: CmsSection | null;
}

export interface ModuleCms<Configuration = CmsPages> {
    configuration: Configuration;
    language: string;
    logicId: string;
    project: string;
    tenant: string;
    type: CMS_ACCESS_TYPE;
}

/**
 * Webapp CMS
 */
export enum AVATAR_SHAPE {
    ROUND = "ROUND",
    SQUARE = "SQUARE",
}

export interface WebappCmsConfigurationAppSettings {
    appName?: string | null;
    appTitle?: string | null;
    avatarFallback?: string | null;
    avatarShape?: AVATAR_SHAPE | null;
    template: {
        colors?: Record<string, string> | null;
        fonts?: {
            url?: string | null;
            primary?: string | null;
            urlSecondary?: string | null;
            secondary?: string | null;
        } | null;
        customCss?: string | null;
    } | null;
    options?: WebappCmsConfigurationAppSettingsOptions;
}

export interface WebappCmsConfigurationAppSettingsOptions {
}

/**
 * Header types & options
 */
export interface SectionHeaderOptions {
    position?: "static" | "fixed";
}

export const CMS_HEADER_IMAGES = [
    "LOGO",
    "LOGO_SECONDARY",
] as const;

export type CMS_HEADER_IMAGE = typeof CMS_HEADER_IMAGES[number];

/**
 * Footer types and options
 */

export const CMS_BANNER_IMAGES = [
    "MAIN"
] as const;

export type CMS_BANNER_IMAGE = typeof CMS_BANNER_IMAGES[number];

export interface WebappCmsConfigurationStructure {
    header?: CmsSection | null;
    footer?: CmsSection | null;
    mainNavigation?: CmsCta[] | null;
    userNavigation?: CmsCta[] | null;
    footerNavigation?: CmsCta[] | null;
    profileNavigation?: CmsCta[] | null;
}

export type CmsPages = Partial<Record<string, CmsPageWrapper>>;

export interface WebappCmsConfiguration {
    appSettings?: WebappCmsConfigurationAppSettings | null;
    structure?: WebappCmsConfigurationStructure | null;
    pages?: CmsPages;
}

export interface WebappCms extends ModuleCms<WebappCmsConfiguration> {

}

/**
 * User CMS
 */
export interface UserCms extends ModuleCms<CmsPages> {

}

/**
 * Contest CMS
 */
export interface ContestCms extends ModuleCms<CmsPages> {

}

/**
 * Event CMS
 */
export interface EventCms extends ModuleCms<CmsPages> {

}

/**
 * Reward CMS
 */
export interface RewardCms extends ModuleCms<CmsPages> {

}
